import {
  TableWrapper,
  CardList,
  Card,
  CardImage,
  CardSupportBadge,
  CardContent,
  CardMainColumns,
  CardLeftColumn,
  CardRightColumn,
  CardIntro,
  CardDetails,
  CardFooter,
  CardField,
  CardFieldLabel,
  CardFieldValue,
  StatusBadge,
  Actions,
  Action,
  PaginationBar,
  PaginationInfo,
  PaginationControls,
  PaginationButton,
} from './TableCard.style';
import { CardImageCarousel } from './CardImageCarousel';
import { InitiativeSupport, InitiativeSupportStatus } from 'types/entities/initiativeSupport';

export type FieldPresentation = 'title' | 'lead' | 'detail' | 'highlight';

export interface TableColumn<T = object> {
  key: string;
  label: string;
  render?: (value: unknown, row: T) => React.ReactNode;
  presentation?: FieldPresentation;
}

export interface TableProps<T = object> {
  columns: TableColumn<T>[];
  data: T[];
  getRowKey?: (row: T, index: number) => string | number;
  onRowClick?: (row: T) => void;
  /** Página atual (1-based). Use junto de `totalPages` e `onPageChange` para exibir paginação. */
  page?: number;
  /** Total de páginas. */
  totalPages?: number;
  onPageChange?: (page: number) => void;
  rowClickable?: boolean;
  initiativeSupports?: InitiativeSupport[];
}

type RowWithImages = {
  id?: string;
  images?: string[];
  title?: string;
};

function getRowImages<T extends object>(row: T): string[] {
  const { images } = row as RowWithImages;
  return Array.isArray(images) ? images : [];
}

function getRowImageMeta<T extends object>(row: T) {
  const { id, title } = row as RowWithImages;
  return { id, alt: title ?? 'Imagem do projeto' };
}

function getSupportBadgeLabel(status: InitiativeSupportStatus): string {
  return status === 'CONFIRMED' ? 'Apoiada' : 'Acompanhada';
}

function renderField<T extends object>(col: TableColumn<T>, row: T) {
  const value = (row as Record<string, unknown>)[col.key];
  const content = col.render ? col.render(value, row) : (value as React.ReactNode);
  const presentation = col.presentation ?? 'detail';

  return (
    <CardField key={col.key} $presentation={presentation}>
      <CardFieldLabel $presentation={presentation}>{col.label}</CardFieldLabel>
      <CardFieldValue
        $presentation={presentation}
        $clampLines={presentation === 'lead' ? 3 : undefined}
      >
        {content}
      </CardFieldValue>
    </CardField>
  );
}

function TableCard<T extends object>({
  columns,
  data,
  onRowClick,
  page,
  totalPages,
  onPageChange,
  rowClickable = false,
  initiativeSupports,
  getRowKey = (row, index) => {
    const r = row as unknown as { id?: string | number };
    return r.id != null ? String(r.id) : index;
  },
}: TableProps<T>) {
  const showPagination =
    page != null && totalPages != null && totalPages > 1 && onPageChange != null;

  const canPrev = showPagination && page > 1;
  const canNext = showPagination && page < totalPages;

  const introColumns = columns.filter(
    (col) => col.presentation === 'title' || col.presentation === 'lead'
  );
  const detailColumns = columns.filter((col) => col.presentation === 'detail');
  const highlightColumns = columns.filter((col) => col.presentation === 'highlight');

  return (
    <TableWrapper>
      <CardList>
        {data.map((row, index) => {
          const { id, alt } = getRowImageMeta(row);
          const images = getRowImages(row);
          const initiativeSupport = initiativeSupports?.find(
            (support) => support.initiativeId === id
          );
          const supportStatus = initiativeSupport?.status;

          return (
            <Card
              key={String(getRowKey(row, index))}
              onClick={() => onRowClick?.(row)}
              $rowClickable={rowClickable}
              $supportStatus={supportStatus}
            >
              <CardImage>
                {supportStatus ? (
                  <CardSupportBadge $status={supportStatus}>
                    {getSupportBadgeLabel(supportStatus)}
                  </CardSupportBadge>
                ) : null}
                <CardImageCarousel
                  initiativeId={id ?? String(getRowKey(row, index))}
                  images={images}
                  alt={alt}
                />
              </CardImage>

              <CardContent>
                <CardMainColumns>
                  <CardLeftColumn>
                    {introColumns.length > 0 ? (
                      <CardIntro>{introColumns.map((col) => renderField(col, row))}</CardIntro>
                    ) : null}

                    {highlightColumns.length > 0 ? (
                      <CardFooter>
                        {highlightColumns.map((col) => renderField(col, row))}
                      </CardFooter>
                    ) : null}
                  </CardLeftColumn>

                  {detailColumns.length > 0 ? (
                    <CardRightColumn>
                      <CardDetails>{detailColumns.map((col) => renderField(col, row))}</CardDetails>
                    </CardRightColumn>
                  ) : null}
                </CardMainColumns>
              </CardContent>
            </Card>
          );
        })}
      </CardList>
      {showPagination && (
        <PaginationBar aria-label="Paginação da tabela">
          <PaginationControls>
            <PaginationButton
              type="button"
              $disabled={!canPrev}
              disabled={!canPrev}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Anterior
            </PaginationButton>
            <PaginationInfo>
              Página {page} de {totalPages}
            </PaginationInfo>
            <PaginationButton
              type="button"
              $disabled={!canNext}
              disabled={!canNext}
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
            >
              Próxima
            </PaginationButton>
          </PaginationControls>
        </PaginationBar>
      )}
    </TableWrapper>
  );
}

TableCard.StatusBadge = StatusBadge;
TableCard.Actions = Actions;
TableCard.Action = Action;

export default TableCard;
